// Pattern: docs/design-patterns/frontend/state-management.md (narrow-store-slices)
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { JiraIssue } from '~/services/api/jira';

interface UIState extends UIStateProperties {
  closeJiraTicketModal: () => void;
  openJiraTicketModal: (ticket: JiraIssue) => void;
  clearPendingPRDAutoStart: () => void;
  openProjectActivity: () => void;
  setActiveProjectAssistantTab: (tab: 'chat' | 'progress') => void;
  /** @deprecated Use startEditingProject/stopEditingProject instead */
  setEditingProject: (id: string | null) => void;
  setEditingTask: (id: string | null) => void;
  setCloningJiraIssueKey: (issueKey: string | null) => void;
  setPendingPRDAutoStart: (projectId: string | null) => void;
  setProjectAssistantExpanded: (expanded: boolean) => void;
  setProjectAssistantWidth: (width: number) => void;
  setSelectedProject: (id: string | null) => void;
  setSelectedRepositoryId: (id: string | null) => void;
  setSelectedTask: (id: string | null) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setNavSidebarCollapsed: (collapsed: boolean) => void;
  setProductsViewMode: (mode: ProductsViewMode) => void;
  setRepositoriesSidebarWidth: (width: number) => void;
  startEditingProject: (id: string) => void;
  stopEditingProject: (id: string) => void;
  toggleProjectAssistant: () => void;
  toggleSidebar: () => void;
  toggleNavSidebar: () => void;
}

interface UIStateProperties {
  activeProjectAssistantTab: 'chat' | 'progress';
  /** Set of project IDs currently being edited (allows multiple simultaneous edits) */
  editingProjectIds: Set<string>;
  editingTaskId: string | null;
  cloningJiraIssueKey: string | null;
  isJiraTicketModalOpen: boolean;
  isProjectAssistantExpanded: boolean;
  isSidebarCollapsed: boolean;
  /**
   * Global shell nav sidebar collapse state (distinct from isSidebarCollapsed,
   * which is bound to the per-feature Products/Repositories sidebars).
   * Persisted via zustand's persist middleware — see the `persist` wrapper
   * and its `partialize`/`merge` config below.
   */
  isNavSidebarCollapsed: boolean;
  selectedJiraTicket: JiraIssue | null;
  /** Project ID that should auto-start PRD generation when panel opens */
  pendingPRDAutoStart: string | null;
  /** Width of the project assistant panel in pixels */
  projectAssistantWidth: number;
  /**
   * Layout mode for the Products page ('grid' or 'list').
   * Persisted via zustand's persist middleware — see the `persist` wrapper
   * and its `partialize`/`merge` config below.
   */
  productsViewMode: ProductsViewMode;
  /** Width of the Repositories sidebar in pixels */
  repositoriesSidebarWidth: number;
  selectedProjectId: string | null;
  selectedRepositoryId: string | null;
  selectedTaskId: string | null;
}

/** Layout mode for the Products page listing. */
export type ProductsViewMode = 'grid' | 'list';

// Default panel width in pixels
const DEFAULT_PANEL_WIDTH = 612;
const DEFAULT_SIDEBAR_WIDTH = 420;

const initialState: UIStateProperties = {
  activeProjectAssistantTab: 'chat',
  editingProjectIds: new Set<string>(),
  editingTaskId: null,
  cloningJiraIssueKey: null,
  isJiraTicketModalOpen: false,
  isProjectAssistantExpanded: false,
  isSidebarCollapsed: false,
  isNavSidebarCollapsed: false,
  selectedJiraTicket: null,
  pendingPRDAutoStart: null,
  projectAssistantWidth: DEFAULT_PANEL_WIDTH,
  productsViewMode: 'grid',
  repositoriesSidebarWidth: DEFAULT_SIDEBAR_WIDTH,
  selectedProjectId: null,
  selectedRepositoryId: null,
  selectedTaskId: null,
};

// NOTE ON ACTION BODY STYLE: every action below uses a block body
// (`(): void => { set(...); }`) rather than the terser expression-body form
// (`(): void => set(...)`). This isn't just a style choice — with `set`'s
// type flowing through `persist(...)`'s curried generics, TypeScript
// resolves an explicit `: void` expression-bodied arrow's `set(...)` result
// to `unknown` (not `void`), which fails to typecheck. A block body sidesteps
// it (the statement's value is discarded, no return-type check applies).
// Matches the existing convention in patternsStore.ts, which persists too.
export const useUIStore = create<UIState>()(
  persist(
    set => ({
      ...initialState,

      setSelectedProject: (id: string | null): void => {
        set({
          selectedProjectId: id,
          // Reset task selection when changing projects
          selectedTaskId: null,
          editingTaskId: null,
        });
      },

      // Legacy method for backwards compatibility - prefer startEditingProject/stopEditingProject
      setEditingProject: (id: string | null): void => {
        set(state => {
          if (id === null) {
            // Clear all editing (legacy behavior)
            return { editingProjectIds: new Set<string>() };
          }
          // Add to editing set
          const newSet = new Set(state.editingProjectIds);
          newSet.add(id);
          return { editingProjectIds: newSet };
        });
      },

      startEditingProject: (id: string): void => {
        set(state => {
          const newSet = new Set(state.editingProjectIds);
          newSet.add(id);
          return { editingProjectIds: newSet };
        });
      },

      stopEditingProject: (id: string): void => {
        set(state => {
          const newSet = new Set(state.editingProjectIds);
          newSet.delete(id);
          return { editingProjectIds: newSet };
        });
      },

      setSelectedTask: (id: string | null): void => {
        set({ selectedTaskId: id });
      },

      setEditingTask: (id: string | null): void => {
        set({ editingTaskId: id });
      },

      toggleSidebar: (): void => {
        set(state => ({ isSidebarCollapsed: !state.isSidebarCollapsed }));
      },

      setSidebarCollapsed: (collapsed: boolean): void => {
        set({ isSidebarCollapsed: collapsed });
      },

      toggleNavSidebar: (): void => {
        set(state => ({ isNavSidebarCollapsed: !state.isNavSidebarCollapsed }));
      },

      setNavSidebarCollapsed: (collapsed: boolean): void => {
        set({ isNavSidebarCollapsed: collapsed });
      },

      toggleProjectAssistant: (): void => {
        set(state => ({
          isProjectAssistantExpanded: !state.isProjectAssistantExpanded,
        }));
      },

      setProjectAssistantExpanded: (expanded: boolean): void => {
        set({ isProjectAssistantExpanded: expanded });
      },

      setProjectAssistantWidth: (width: number): void => {
        set({ projectAssistantWidth: width });
      },

      setActiveProjectAssistantTab: (tab: 'chat' | 'progress'): void => {
        set({ activeProjectAssistantTab: tab });
      },

      openProjectActivity: (): void => {
        set({
          isProjectAssistantExpanded: true,
          activeProjectAssistantTab: 'progress',
        });
      },

      openJiraTicketModal: (ticket: JiraIssue): void => {
        set({ isJiraTicketModalOpen: true, selectedJiraTicket: ticket });
      },

      closeJiraTicketModal: (): void => {
        set({ isJiraTicketModalOpen: false, selectedJiraTicket: null });
      },

      setCloningJiraIssueKey: (issueKey: string | null): void => {
        set({ cloningJiraIssueKey: issueKey });
      },

      setPendingPRDAutoStart: (projectId: string | null): void => {
        set({ pendingPRDAutoStart: projectId });
      },

      clearPendingPRDAutoStart: (): void => {
        set({ pendingPRDAutoStart: null });
      },

      setSelectedRepositoryId: (id: string | null): void => {
        set({ selectedRepositoryId: id });
      },

      setRepositoriesSidebarWidth: (width: number): void => {
        set({ repositoriesSidebarWidth: width });
      },

      setProductsViewMode: (mode: ProductsViewMode): void => {
        set({ productsViewMode: mode });
      },
    }),
    {
      name: 'compounds-shell-ui',
      version: 1,
      // Persist ONLY the collapse flag and the products view mode. The store
      // also holds non-serializable state (editingProjectIds: Set) and
      // ephemeral modal/selection/PRD-draft flags that must never survive a
      // reload.
      partialize: state => ({
        isNavSidebarCollapsed: state.isNavSidebarCollapsed,
        productsViewMode: state.productsViewMode,
      }),
      // Defend against a corrupted/legacy localStorage value (wrong type or
      // shape): fall back to the initial values instead of throwing or
      // injecting an invalid value into state. Mirrors patternsStore's merge.
      merge: (persisted, current) => {
        const p = persisted as Record<string, unknown> | undefined;
        const isNavSidebarCollapsed =
          p && typeof p['isNavSidebarCollapsed'] === 'boolean'
            ? p['isNavSidebarCollapsed']
            : current.isNavSidebarCollapsed;
        const productsViewMode =
          p && (p['productsViewMode'] === 'grid' || p['productsViewMode'] === 'list')
            ? p['productsViewMode']
            : current.productsViewMode;
        return { ...current, isNavSidebarCollapsed, productsViewMode };
      },
    },
  ),
);
